import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Subscription.css';

const Paymentorship = () => {
  const { currentUser, showToast } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [amountGHS, setAmountGHS] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [isLoading, setIsLoading] = useState(false);
  const [exchangeRateToGHS, setExchangeRateToGHS] = useState(1);
  const [rateLoading, setRateLoading] = useState(false);
  const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;

  useEffect(() => {
    document.title = 'Private Mentorship Payment | LightTrade FOREX Academy';

    const robotsMeta = document.createElement('meta');
    robotsMeta.name = 'robots';
    robotsMeta.content = 'noindex,nofollow,noarchive,nosnippet,noimageindex';
    document.head.appendChild(robotsMeta);

    return () => {
      document.head.removeChild(robotsMeta);
    };
  }, []);

  useEffect(() => {
    if (!window.PaystackPop) {
      const script = document.createElement('script');
      script.src = 'https://js.paystack.co/v1/inline.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    const parseAmountAndCurrency = () => {
      const rawSearch = (location.search || '').replace(/^\?/, '');
      const tokens = rawSearch
        .split('&')
        .map((token) => decodeURIComponent(token).trim())
        .filter(Boolean);

      const amountToken = tokens.find((token) => /^\d+(\.\d+)?$/.test(token));
      const currencyToken = tokens.find((token) => /^[a-z]{3}$/i.test(token));

      if (amountToken) {
        setAmountGHS(amountToken);
      }

      if (currencyToken) {
        setCurrency(currencyToken.toUpperCase());
      }
    };

    parseAmountAndCurrency();
  }, [location.search]);

  useEffect(() => {
    const fetchExchangeRate = async () => {
      if (currency === 'GHS') {
        setExchangeRateToGHS(1);
        return;
      }

      setRateLoading(true);
      try {
        const rateResponse = await fetch(`https://open.er-api.com/v6/latest/${currency}`);
        const rateData = await rateResponse.json();

        if (rateData.result === 'success' && Number.isFinite(rateData?.rates?.GHS)) {
          setExchangeRateToGHS(rateData.rates.GHS);
        } else {
          throw new Error('Could not fetch exchange rate.');
        }
      } catch (error) {
        console.error('Exchange rate fetch error:', error);
        showToast(`Could not fetch ${currency} conversion rate. Please try again.`, 'error');
        setExchangeRateToGHS(0);
      } finally {
        setRateLoading(false);
      }
    };

    fetchExchangeRate();
  }, [currency, showToast]);

  const handleMentorshipPayment = () => {
    if (isLoading) return;

    if (!currentUser?.email || !currentUser?.uid) {
      showToast('Please sign in again before starting payment.', 'error');
      return;
    }

    if (!PAYSTACK_PUBLIC_KEY) {
      showToast('Payment service is not configured. Please contact support.', 'error');
      return;
    }

    const numericAmount = Number(amountGHS);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      showToast('Please enter a valid agreed amount.', 'error');
      return;
    }

    if (!Number.isFinite(exchangeRateToGHS) || exchangeRateToGHS <= 0) {
      showToast('Currency conversion is unavailable. Please try again shortly.', 'error');
      return;
    }

    if (!window.PaystackPop) {
      showToast('Payment service is not available. Please refresh the page.', 'error');
      return;
    }

    const convertedAmountGHS = Math.max(1, Math.round(numericAmount * exchangeRateToGHS));

    setIsLoading(true);
    const handler = window.PaystackPop.setup({
      key: PAYSTACK_PUBLIC_KEY,
      email: currentUser.email,
      amount: convertedAmountGHS * 100,
      currency: 'GHS',
      ref: `LTF-MENTORSHIP-${Date.now()}`,
      metadata: {
        userId: currentUser.uid,
        plan: 'mentorship',
        type: 'mentorship',
        originalAmount: numericAmount,
        originalCurrency: currency,
      },
      callback: (response) => {
        showToast('Payment completed! Verifying transaction...', 'info');
        const verifyMentorshipPayment = async () => {
          try {
            const verifyToken = await currentUser.getIdToken();
            const verifyRes = await fetch('/api/verify-paystack', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${verifyToken}`,
              },
              body: JSON.stringify({
                reference: response.reference,
                type: 'mentorship',
                plan: 'mentorship',
                userId: currentUser.uid,
              }),
            });
            const verifyData = await verifyRes.json();

            if (!verifyRes.ok) {
              throw new Error(verifyData.error || 'Failed to verify payment');
            }

            const paymentSuccessful =
              verifyData?.data?.status === 'success' ||
              verifyData?.status === 'success' ||
              verifyData?.status === true;

            if (!paymentSuccessful) {
              throw new Error(verifyData.message || verifyData.error || 'Payment verification failed');
            }

            showToast('Mentorship payment verified successfully.', 'success');

            const emailIdToken = await currentUser.getIdToken();
            await fetch('/api/send-email-invoice', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${emailIdToken}`,
              },
              body: JSON.stringify({
                type: 'mentorship',
                email: currentUser.email,
                name: currentUser.displayName || currentUser.email.split('@')[0],
                paymentData: {
                  reference: response.reference,
                  amount: verifyData?.data?.amount ? verifyData.data.amount / 100 : convertedAmountGHS,
                  currency: verifyData?.data?.currency || 'GHS',
                  paidAt: new Date().toISOString(),
                },
                planDetails: {
                  name: '1-on-1 Mentorship (One Month)',
                  price: numericAmount,
                  currency,
                  convertedAmountGHS,
                },
              }),
            });

            window.location.href = '/program?payment=mentorship-success';
          } catch (error) {
            showToast(
              `Payment completed but verification needs support: ${error.message}. Reference: ${response.reference}`,
              'error',
              { autoClose: 10000 }
            );
          } finally {
            setIsLoading(false);
          }
        };

        verifyMentorshipPayment();
      },
      onClose: () => {
        setIsLoading(false);
      },
    });

    handler.openIframe();
  };

  return (
    <div className="subscription-page">
      <div className="subscription-header">
        <h1>Private 1-on-1 Mentorship Payment</h1>
        <p>Live chat + personalized trading guidance. Enter your agreed one-time amount to continue.</p>
      </div>

      <div className="plans-container">
        <div className="plan-card">
          <div className="plan-card-content">
            <div className="plan-card-header">
              <h2>1-on-1 Mentorship</h2>
              <div className="price">
                <div className="mentorship-price-input">
                  <label htmlFor="paymentorship-amount">Agreed amount</label>
                  <input
                    id="paymentorship-amount"
                    type="number"
                    min="1"
                    step="1"
                    value={amountGHS}
                    onChange={(e) => setAmountGHS(e.target.value)}
                    placeholder="Enter amount"
                  />
                  <label htmlFor="paymentorship-currency" style={{ marginTop: '10px' }}>Currency</label>
                  <select
                    id="paymentorship-currency"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd' }}
                  >
                    <option value="USD">USD</option>
                    <option value="GHS">GHS</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                  <small>One-time payment for one month.</small>
                  {rateLoading ? (
                    <small>Fetching exchange rate...</small>
                  ) : (
                    <small>
                      You will be charged in GHS: {Number.isFinite(Number(amountGHS))
                        ? `${Math.max(1, Math.round(Number(amountGHS) * exchangeRateToGHS)).toLocaleString()} GHS`
                        : '--'}
                    </small>
                  )}
                </div>
              </div>
              <ul className="features">
                <li>Private one-on-one mentorship (1 month)</li>
                <li>Direct strategy guidance and review</li>
                <li>Personalized support based on your package</li>
              </ul>
            </div>

            <div className="plan-card-footer">
              <button className="select-plan-btn" onClick={handleMentorshipPayment} disabled={isLoading || !amountGHS || rateLoading}>
                {isLoading ? 'Processing...' : 'Pay One-Time'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="back-to-program-section">
        <button onClick={() => navigate('/program')} className="back-to-program-btn">
          Go to My Program
        </button>
      </div>
    </div>
  );
};

export default Paymentorship;
